import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  TextField,
  useMediaQuery,
} from '@mui/material';

import { ReactComponent as SearchArrowIcon } from '../../img/llm/search_arrow.svg';
import { INVESTIGATE_ATTACH_NOTE, pastedAttachments } from '../../service/attachments';
import { trackGtagEvent } from '../../utils/gtag';
import {
    AttachButton,
    AttachmentChips,
    attachmentBlockedNote,
    dragHasFiles,
    filesFromTransfer,
} from '../Units/AttachmentChips';
import TierPicker from '../Units/TierPicker';

const ChatSearchBar = ({
    userInput,
    setUserInput,
    isLoading,
    isRunElsewhere = false,
    isQueryLimitReached = false,
    // Investigate is fixed for the life of a session, so the bar reports the
    // mode for analytics but no longer renders a toggle.
    investigateEnabled = false,
    // Which service tier answers the next question (service/serviceTiers.js). Per-turn, unlike
    // `investigateEnabled`: the picker is right here in the composer, so a reader can change it
    // between two turns of one conversation and expects the next answer to honour the change.
    serviceTier,
    onServiceTierChange,
    onServiceTierResolveDefault,
    // A guest is held to Standard; picking another tier asks them to sign in.
    isGuest = false,
    onRequireSignIn,
    // Resolved by the parent, which is the only place that can see both signals — see the
    // comment at the call site. Not derived from `investigateEnabled` above: that one is
    // for analytics and is false for a reopened investigate conversation.
    pipelineIsDeepResearch = false,
    /* The files waiting on this question (Units/AttachmentChips/useAttachments), owned by the
       parent because it reads them at send time. Absent: no paperclip at all. */
    attachments = null,
    // A guest reaching for the paperclip (or dropping a file) is asked to sign in.
    onAttachRequireSignIn,
    onSubmit,
    onStop,
}) => {
    const isMobileViewport = useMediaQuery('(max-width:767px)');
    const [isDragOver, setIsDragOver] = useState(false);
    /* Two different situations wear the same `isLoading`, and the field stays usable in both.

       THIS conversation is answering: a submit is queued by the parent, so a follow-up that
       occurs to the reader mid-answer leaves their hands at once instead of being held in
       their head for the length of a run. A second turn here really would race the first.

       ANOTHER conversation is answering (`isRunElsewhere`): nothing is racing. That run has
       its own session and its own history id, and the backend locks per history id — so this
       question simply starts, and the other answer goes on being written. The field used to
       be disabled here, which meant "New Chat" during a run led to a composer that could not
       be typed in. */
    const canType = !isQueryLimitReached;
    /* Attachments are AI Chat only (the Investigate pipeline does not read them), and a guest's
       question has no owner to keep a file for — so the "+" button is off on one and asks the
       other to sign in. Chips already waiting when the conversation is an Investigate one
       hold the send back with a note rather than being dropped without a word. */
    const attachDisabled = pipelineIsDeepResearch || !canType;
    const attachmentNote = attachmentBlockedNote(attachments, { investigate: pipelineIsDeepResearch });
    const readyAttachmentCount = attachments?.readyAttachments?.length || 0;
    const canSend = (Boolean(userInput.trim()) || readyAttachmentCount > 0)
        && canType
        && !attachments?.isUploading
        && !attachmentNote;
    const takeFiles = (files) => {
        if (!attachments || !files?.length) return;
        if (isGuest) {
            onAttachRequireSignIn?.();
            return;
        }
        if (attachDisabled) return;
        attachments.addFiles(files);
    };
    // Stop is what the button offers when there is nothing to send. Typing turns it back into
    // send, which is also how a reader gets out of a queued follow-up they no longer want:
    // clear the field and the stop control is there again. There is nothing here to stop when
    // the run belongs to another thread.
    const showStop = isLoading && !isRunElsewhere && !canSend;
    const trackInvestigateSubmit = (inputMethod) => {
        if (!pipelineIsDeepResearch) return;
        trackGtagEvent('investigate_question_submit', {
            source: 'chat_searchbar',
            input_method: inputMethod,
            queued: Boolean(isLoading && !isRunElsewhere),
        });
    };
    const placeholder = isRunElsewhere
        ? (isMobileViewport ? 'Ask something new…' : 'Ask a new question — the other answer keeps writing')
        : (isLoading
            ? (isMobileViewport ? 'Ask next…' : 'Ask a follow-up — it will send when this answer finishes')
            : (isMobileViewport ? 'Ask more...' : 'Ask a question about the biomedical literature...'));

    return (
        <div className="chat-header">
        <Box
            className={isDragOver ? 'attachment-drop-target' : undefined}
            onDragOver={(event) => {
                if (!attachments || !dragHasFiles(event)) return;
                event.preventDefault();
                if (!isDragOver) setIsDragOver(true);
            }}
            onDragLeave={(event) => {
                if (event.currentTarget.contains(event.relatedTarget)) return;
                setIsDragOver(false);
            }}
            onDrop={(event) => {
                if (!attachments || !dragHasFiles(event)) return;
                event.preventDefault();
                setIsDragOver(false);
                takeFiles(filesFromTransfer(event.dataTransfer));
            }}
            sx={{
            width: '100%',
            display: 'flex',
            // The chips sit above the field, inside the same rounded box.
            flexDirection: 'column',
            margin: '0 auto',
            backgroundColor: 'var(--color-background-subtle)',
            borderRadius: '16px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--color-border-default)',
            boxShadow: 'none',
        }}>
            {attachments && (
                <AttachmentChips
                    items={attachments.items}
                    notice={attachments.notice}
                    blockedNote={attachmentNote}
                    onRemove={attachments.remove}
                />
            )}
            <TextField
                className="input-form"
                size="small"
                value={userInput}
                onPaste={(event) => {
                    /* A paste that carries files (any type) attaches them; a very long text
                       becomes "Pasted text.txt", as in Claude — but only where a file could be
                       attached, so a guest's or an Investigate paste still lands in the box. */
                    if (!attachments) return;
                    const files = pastedAttachments(event.clipboardData, {
                        longText: !isGuest && !attachDisabled,
                    });
                    if (!files.length) return;
                    event.preventDefault();
                    takeFiles(files);
                }}
                onChange={(e) => {
                    if (!canType) return;
                    setUserInput(e.target.value);
                }}
                disabled={!canType}
                variant="outlined"
                placeholder={placeholder}
                multiline
                minRows={1}
                maxRows={4}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
                        e.preventDefault();
                        if (canSend) {
                            trackInvestigateSubmit('enter');
                            onSubmit?.(e, { queryMethod: 'enter' });
                        }
                    }
                }}
                sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                        borderRadius: '16px',
                        minHeight: isMobileViewport ? '44px' : '52px',
                        height: 'auto',
                        alignItems: 'center',
                        // 20px of text inset, or room for the "+" when it leads the field.
                        paddingLeft: attachments ? '8px' : '20px',
                        /* The end cluster (model chip, clear, send) is a flex sibling of the
                           textarea rather than an overlay, so the field's own right padding is
                           just the gap to the composer's edge. It used to be 60px to clear an
                           absolutely-positioned send button — a reservation that could only ever
                           be right for one cluster width, and the chip's width depends on the
                           model's name. */
                        paddingRight: '12px !important',
                        gap: '8px',
                        paddingTop: isMobileViewport ? '8px' : '10px',
                        paddingBottom: isMobileViewport ? '8px' : '10px',
                        fontFamily: 'Geist, sans-serif',
                        fontSize: '14px',
                        color: 'var(--color-text-primary)',
                        '& fieldset': {
                            border: 'none',
                        },
                    },
                    '& .MuiInputBase-input': {
                        lineHeight: '24px',
                    },
                    '& .MuiInputBase-input::placeholder': {
                        color: 'var(--color-grey-300)',
                        opacity: 1,
                    },
                }}
                fullWidth
                InputProps={{
                    // The "+" panel opens under (or over) the whole field, as ChatGPT's does.
                    'data-attach-anchor': true,
                    // The "+" at the start of the field, as in ChatGPT.
                    startAdornment: attachments ? (
                        <AttachButton
                            onFiles={takeFiles}
                            isGuest={isGuest}
                            onRequireSignIn={onAttachRequireSignIn}
                            disabled={attachDisabled}
                            disabledReason={pipelineIsDeepResearch ? INVESTIGATE_ATTACH_NOTE : ''}
                            source="chat_searchbar"
                        />
                    ) : null,
                    endAdornment: (
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                                gap: 1,
                                // The textarea is what gives way when the row is tight; this
                                // cluster is all fixed-size controls.
                                flexShrink: 0,
                            }}
                        >
                            {/* On the field's own row, left of send — where the home page's bar
                                puts it too. It had a control row of its own under the field for
                                a while, which cost the composer 54px of height for one chip and
                                left the chip stranded in a band of empty space. */}
                            <TierPicker
                                value={serviceTier}
                                onChange={onServiceTierChange}
                                onResolveDefault={onServiceTierResolveDefault}
                                pipeline={pipelineIsDeepResearch ? 'deep_research' : 'chat'}
                                isGuest={isGuest}
                                onRequireSignIn={onRequireSignIn}
                                // Left usable while an answer streams. A follow-up typed
                                // mid-answer is queued by the parent, and it should be able to
                                // name its own tier — the choice applies to the NEXT request,
                                // never to the one in flight.
                                disabled={isQueryLimitReached}
                            />
                            {userInput !== '' && !isQueryLimitReached && !isLoading && (
                                <CloseIcon
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                    }}
                                    onClick={() => {
                                        trackGtagEvent('chat_input_clear_click', { source: 'chat_searchbar' });
                                        setUserInput('');
                                    }}
                                    sx={{
                                        color: 'grey.500',
                                        cursor: 'pointer',
                                        fontSize: '20px',
                                    }}
                                />
                            )}
                            {showStop ? (
                                <Box
                                    component="button"
                                    type="button"
                                    aria-label="Stop generating"
                                    onClick={() => {
                                        trackGtagEvent('chat_stop_click', { source: 'chat_searchbar' });
                                        onStop?.();
                                    }}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        // Same brand-muted square as the idle send button — the
                                        // near-black fill it used to have belonged to no palette here.
                                        backgroundColor: 'var(--color-brand-muted)',
                                        border: 'none',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                    title="Stop"
                                >
                                    <Box sx={{ width: 12, height: 12, backgroundColor: 'var(--color-brand-primary)', borderRadius: '2px' }} />
                                </Box>
                            ) : (
                                <Box
                                    component="button"
                                    type="button"
                                    aria-label={isLoading ? 'Send when this answer finishes' : 'Send'}
                                    aria-disabled={!canSend}
                                    onClick={(event) => {
                                        if (!canSend) return;
                                        trackGtagEvent('chat_submit_click', {
                                            source: 'chat_searchbar',
                                            investigate: Boolean(investigateEnabled),
                                            queued: false,
                                        });
                                        trackInvestigateSubmit('button');
                                        onSubmit?.(event, { queryMethod: 'button' });
                                    }}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        backgroundColor: canSend ? 'var(--color-brand-primary)' : 'var(--color-brand-muted)',
                                        border: 'none',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: canSend ? 'pointer' : 'default',
                                    }}
                                    title={isLoading ? 'Send when this answer finishes' : 'Send'}
                                >
                                    <SearchArrowIcon
                                        style={{
                                            width: 16,
                                            height: 16,
                                            color: canSend ? 'var(--color-neutral-white)' : 'var(--color-brand-primary)',
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    ),
                }}
            />
        </Box>
        </div>
    );
};

export default ChatSearchBar;
